import { useState } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { CheckCircle2, FileUp, Music } from 'lucide-react';
import clsx from 'clsx';
import { apiClient, ApiError } from '@/lib/api';
import { isAdminRole, useAuthStore } from '@/lib/auth-store';
import type {
  CourseAdminView,
  CourseImportPrecheck,
  CourseImportResult,
  CourseImportStrategy,
  CourseListEnvelope,
  CourseStatus,
} from '@/lib/types';
import { describeCourseAudio, uploadCourseAudioApi } from '@/lib/course-audio-api';
import { exportCourseMarkdown } from '@/lib/export-api';
import { formatFileSize } from '@/lib/format';
import { Card } from '@/components/ui/card';
import { Modal } from '@/components/ui/modal';
import { EmptyState } from '@/components/ui/empty-state';
import { KnowledgeEntriesWorkspace } from '@/components/knowledge/knowledge-entries';
import { CourseCatalog } from '@/components/knowledge/course-catalog';
import { SegmentConfigModal } from '@/components/knowledge/segment-config-modal';
import {
  DEFAULT_COURSE_FILTER,
  buildCourseListQuery,
  type CourseCatalogFilterState,
} from '@/lib/course-catalog-filters';
import { SegmentedTabs } from '@/components/ui/segmented-tabs';
import { resolveActiveTab } from '@/lib/page-tabs';
import {
  DeleteCaptchaField,
  useDeleteCaptcha,
} from '@/components/ui/delete-captcha';

// Issue #118 / #125 返修：管理端知识库页 = 真实课程管理（CRUD + 按课程导入）。
// 所有写操作调用真实 /admin/courses* 接口（ADMIN+ / orgId 隔离 / AuditLog），
// 成功后刷新列表；不使用任何前端假状态。

type ModalState =
  | { kind: 'create' }
  | { kind: 'edit'; course: CourseAdminView }
  | { kind: 'audio'; course: CourseAdminView }
  | { kind: 'import'; course: CourseAdminView }
  | { kind: 'segments'; course: CourseAdminView }
  | { kind: 'delete'; course: CourseAdminView }
  | null;

const PRIMARY_BTN =
  'inline-flex items-center gap-2 rounded-md bg-brand-500 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-brand-600 disabled:opacity-60';
const SECONDARY_BTN =
  'inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-600 transition hover:bg-slate-50 disabled:opacity-60';
const DANGER_BTN =
  'inline-flex items-center gap-2 rounded-md bg-rose-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-rose-700 disabled:opacity-60';
const INPUT =
  'w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100';

export function KnowledgeBasePage() {
  const user = useAuthStore((s) => s.user);
  const isAdmin = isAdminRole(user?.role);
  const queryClient = useQueryClient();
  const [modal, setModal] = useState<ModalState>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // Issue #221：分区卡片——一次只展示一个版块。
  // Issue #226：移除知识库内重复的「登录日志」分区；登录日志唯一入口在系统设置。
  const KB_TABS = [
    { key: 'courses', label: '课程目录' },
    { key: 'entries', label: '知识库条目' },
  ] as const;
  type KbTab = (typeof KB_TABS)[number]['key'];
  const [tab, setTab] = useState<KbTab>('courses');
  const activeTab = resolveActiveTab<KbTab>(tab, KB_TABS.map((t) => t.key));

  // Issue #345：课程目录筛选状态（驱动后端真实分页 + 搜索 + 排序）。
  const [courseFilter, setCourseFilter] = useState<CourseCatalogFilterState>(
    DEFAULT_COURSE_FILTER,
  );
  const courseQueryParams = buildCourseListQuery(courseFilter);

  const query = useQuery({
    // queryKey 含查询参数：翻页 / 搜索 / 排序变化即重新请求对应页。
    queryKey: ['admin', 'courses', courseQueryParams],
    enabled: isAdmin,
    // 真实课程管理数据源（ADMIN+），后端分页 { items, pagination }，含停用课程与有效题量。
    queryFn: () =>
      apiClient.get<CourseListEnvelope>(
        '/admin/courses',
        courseQueryParams as unknown as Record<string, unknown>,
      ),
    // 翻页时保留上一页结果，避免闪烁（真实分页，仍以最新响应为准）。
    placeholderData: keepPreviousData,
  });

  // 前缀失效：命中 ['admin','courses', *] 所有分页 / 筛选变体，写操作后刷新当前页。
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['admin', 'courses'] });
  const closeModal = () => setModal(null);

  // 启用 / 停用：可逆动作，直接按钮触发（不弹确认框）。
  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: CourseStatus }) =>
      apiClient.post<CourseAdminView>(`/admin/courses/${id}/status`, { status }),
    onSuccess: async () => {
      setActionError(null);
      await refresh();
    },
    onError: (err) =>
      setActionError(err instanceof ApiError ? err.message : '操作失败，请重试'),
  });

  /**
   * Issue #7：导出课程 Markdown。
   *
   * 走真实后端 GET /admin/courses/:id/export（ADMIN+ / orgId 隔离 / 有效题目 / 顺序一致），
   * 由 ApiClient.download 强制校验 HTTP 状态与 content-type 后再落盘 —— 绝不用当前页
   * state 拼接假文件，也绝不把 HTML 错误页当成 .md 存下来。失败展示后端中文原因。
   */
  const exportMutation = useMutation({
    mutationFn: (course: CourseAdminView) =>
      exportCourseMarkdown(course.id, `${course.name}.md`),
    onSuccess: () => setActionError(null),
    onError: (err) =>
      setActionError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : '课程导出失败，请重试',
      ),
  });

  if (!isAdmin) {
    return (
      <div className="flex flex-col gap-6">
        <Header />
        <Card title="课程与知识库">
          <EmptyState
            title="当前角色无权管理课程"
            description="课程管理仅 ADMIN 及以上角色可用。如需管理课程与知识库，请使用管理员账号登录。"
          />
        </Card>
      </div>
    );
  }

  const items = query.data?.items ?? [];

  return (
    <div className="flex flex-col gap-6">
      <Header />

      {/* Issue #221：分区卡片 / Tab —— 一次只展示一个版块（课程目录 / 知识库条目）。 */}
      <SegmentedTabs
        items={KB_TABS}
        value={activeTab}
        onChange={(k) => setTab(k)}
        data-testid="kb-tabs"
      />

      {actionError ? (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          {actionError}
        </div>
      ) : null}

      {activeTab === 'courses' ? (
        /* Issue #221：课程目录改为高级搜索 / 列隐藏 / 分页（默认 20/页）表格。Issue #226：删除入口仍走 CourseCatalog → DeleteConfirmModal（已接入删除验证码）。 */
        <CourseCatalog
          courses={items}
          pagination={query.data?.pagination}
          filter={courseFilter}
          onFilterChange={(patch) =>
            setCourseFilter((prev) => ({ ...prev, ...patch }))
          }
          loading={query.isLoading}
          error={
            query.isError
              ? query.error instanceof Error
                ? query.error.message
                : '课程接口加载失败'
              : null
          }
          onRetry={() => void query.refetch()}
          statusPendingId={
            statusMutation.isPending ? statusMutation.variables?.id ?? null : null
          }
          exportPendingId={
            exportMutation.isPending ? exportMutation.variables?.id ?? null : null
          }
          onCreate={() => {
            setActionError(null);
            setModal({ kind: 'create' });
          }}
          onEdit={(course) => {
            setActionError(null);
            setModal({ kind: 'edit', course });
          }}
          onAudio={(course) => {
            setActionError(null);
            setModal({ kind: 'audio', course });
          }}
          onImport={(course) => {
            setActionError(null);
            setModal({ kind: 'import', course });
          }}
          onExport={(course) => {
            setActionError(null);
            exportMutation.mutate(course);
          }}
          onSegments={(course) => {
            setActionError(null);
            setModal({ kind: 'segments', course });
          }}
          onToggleStatus={(course) =>
            statusMutation.mutate({
              id: course.id,
              status: course.status === 'ENABLED' ? 'DISABLED' : 'ENABLED',
            })
          }
          onDelete={(course) => {
            setActionError(null);
            setModal({ kind: 'delete', course });
          }}
        />
      ) : (
        /* Issue #179：知识库条目工作区（高级检索 / 列隐藏 / 分页 / CRUD / 详情）。 */
        <KnowledgeEntriesWorkspace />
      )}

      {/* 弹窗按 key 重挂载（架构红线 6.2）：切换目标课程时强制重置内部表单状态。 */}
      {modal?.kind === 'create' ? (
        <CourseFormModal key="create" onClose={closeModal} onDone={refresh} />
      ) : null}
      {modal?.kind === 'edit' ? (
        <CourseFormModal
          key={`edit-${modal.course.id}`}
          course={modal.course}
          onClose={closeModal}
          onDone={refresh}
        />
      ) : null}
      {modal?.kind === 'audio' ? (
        <AudioModal
          key={`audio-${modal.course.id}`}
          course={modal.course}
          onClose={closeModal}
          onDone={refresh}
        />
      ) : null}
      {modal?.kind === 'import' ? (
        <ImportModal
          key={`import-${modal.course.id}`}
          course={modal.course}
          onClose={closeModal}
          onDone={refresh}
        />
      ) : null}
      {modal?.kind === 'segments' ? (
        <SegmentConfigModal
          key={`segments-${modal.course.id}`}
          course={modal.course}
          onClose={closeModal}
          onDone={refresh}
        />
      ) : null}
      {modal?.kind === 'delete' ? (
        <DeleteConfirmModal
          key={`delete-${modal.course.id}`}
          course={modal.course}
          onClose={closeModal}
          onDone={refresh}
        />
      ) : null}
    </div>
  );
}

function Header() {
  return (
    <header>
      <div className="text-xs text-slate-500">培训中心 / 知识库</div>
      <h1 className="mt-1 text-2xl font-semibold text-slate-900">课程与知识库</h1>
    </header>
  );
}

function CourseFormModal({
  course,
  onClose,
  onDone,
}: {
  course?: CourseAdminView;
  onClose: () => void;
  onDone: () => Promise<unknown> | void;
}) {
  const editing = !!course;
  const [name, setName] = useState(course?.name ?? '');
  const [code, setCode] = useState(course?.code ?? '');
  const [description, setDescription] = useState(course?.description ?? '');
  const [sortOrder, setSortOrder] = useState(String(course?.sortOrder ?? 0));
  const [status, setStatus] = useState<CourseStatus>(course?.status ?? 'ENABLED');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => {
      const sort = Number(sortOrder);
      const base = {
        name: name.trim(),
        code: code.trim(),
        description: description.trim() ? description.trim() : undefined,
        sortOrder: Number.isFinite(sort) ? sort : 0,
      };
      if (editing) {
        return apiClient.patch<CourseAdminView>(`/admin/courses/${course!.id}`, base);
      }
      return apiClient.post<CourseAdminView>('/admin/courses', { ...base, status });
    },
    onSuccess: async () => {
      await onDone();
      onClose();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : '保存失败，请重试'),
  });

  const disabled = mutation.isPending || !name.trim() || !code.trim();

  return (
    <Modal
      title={editing ? '编辑课程' : '新增课程'}
      description={editing ? `课程 ID：${course!.id}` : '创建一门新课程，随后可按课程导入知识库。'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={SECONDARY_BTN} onClick={onClose}>
            取消
          </button>
          <button
            type="submit"
            form="course-form"
            className={PRIMARY_BTN}
            disabled={disabled}
          >
            {mutation.isPending ? '保存中…' : '保存'}
          </button>
        </>
      }
    >
      <form
        id="course-form"
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          mutation.mutate();
        }}
      >
        <label className="block text-xs text-slate-600">
          <span className="mb-1 block">课程名称 *</span>
          <input
            className={INPUT}
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={128}
            placeholder="如：细胞基础知识"
          />
        </label>
        <label className="block text-xs text-slate-600">
          <span className="mb-1 block">课程编码 *（字母 / 数字 / - / _）</span>
          <input
            className={INPUT}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            maxLength={64}
            placeholder="如：cell-basics"
          />
        </label>
        <label className="block text-xs text-slate-600">
          <span className="mb-1 block">课程简介</span>
          <textarea
            className={INPUT}
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={2048}
          />
        </label>
        <div className="flex gap-3">
          <label className="block flex-1 text-xs text-slate-600">
            <span className="mb-1 block">排序值</span>
            <input
              className={INPUT}
              type="number"
              min={0}
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            />
          </label>
          {!editing ? (
            <label className="block flex-1 text-xs text-slate-600">
              <span className="mb-1 block">初始状态</span>
              <select
                className={INPUT}
                value={status}
                onChange={(e) => setStatus(e.target.value as CourseStatus)}
              >
                <option value="ENABLED">启用</option>
                <option value="DISABLED">停用</option>
              </select>
            </label>
          ) : null}
        </div>
        {error ? (
          <div className="rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-xs text-rose-700">
            {error}
          </div>
        ) : null}
      </form>
    </Modal>
  );
}

// ─── 软删除确认 ──────────────────────────────────────────────────────────
function DeleteConfirmModal({
  course,
  onClose,
  onDone,
}: {
  course: CourseAdminView;
  onClose: () => void;
  onDone: () => Promise<unknown> | void;
}) {
  const [error, setError] = useState<string | null>(null);
  // Issue #235：删除前需通过后端签发的 6 位图形验证码。
  const captcha = useDeleteCaptcha('course:delete');
  const [captchaCode, setCaptchaCode] = useState('');
  const mutation = useMutation({
    mutationFn: () =>
      apiClient.delete<{ id: string; deleted: boolean }>(`/admin/courses/${course.id}`, {
        captchaId: captcha.captchaId,
        captchaCode,
      }),
    onSuccess: async () => {
      await onDone();
      onClose();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : '删除失败，请重试');
      // 验证码一次性：失败后作废，刷新一枚并清空输入，让用户重试。
      if (err instanceof ApiError && err.status === 400) {
        setCaptchaCode('');
        void captcha.refresh();
      }
    },
  });

  const canDelete = !mutation.isPending && !!captcha.captchaId && captchaCode.trim().length === 6;

  return (
    <Modal
      title="软删除课程"
      description={`确认删除「${course.name}」？`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={SECONDARY_BTN} onClick={onClose}>
            取消
          </button>
          <button
            type="button"
            className={DANGER_BTN}
            disabled={!canDelete}
            onClick={() => {
              setError(null);
              mutation.mutate();
            }}
          >
            {mutation.isPending ? '删除中…' : '确认删除'}
          </button>
        </>
      }
    >
      <p className="text-sm text-slate-600">
        软删除后该课程将从员工端隐藏，但<strong>保留</strong>已有题库、培训记录与答题历史，可由后端恢复。
      </p>
      <DeleteCaptchaField
        svg={captcha.svg}
        loading={captcha.loading}
        error={captcha.error}
        value={captchaCode}
        onChange={setCaptchaCode}
        onRefresh={captcha.refresh}
      />
      {error ? (
        <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-xs text-rose-700">
          {error}
        </div>
      ) : null}
    </Modal>
  );
}

// ─── 课程音频导入（Issue #316：真实 multipart 上传 MP3，blue-white UI） ──────
function AudioModal({
  course,
  onClose,
  onDone,
}: {
  course: CourseAdminView;
  onClose: () => void;
  onDone: () => Promise<unknown> | void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  // 本次上传成功后的新音频（用于即时刷新「当前状态」，无需等列表查询回填）。
  const [uploadedAudio, setUploadedAudio] = useState<CourseAdminView['audio']>(null);

  const mutation = useMutation({
    // 红线：真实 multipart 上传到后端，绝不前端假成功、绝不把二进制塞进 JSON 文本。
    mutationFn: () => uploadCourseAudioApi(course.id, file as File),
    onSuccess: async (data) => {
      setError(null);
      setUploadedAudio(data.audio);
      setFile(null);
      await onDone();
    },
    onError: (err) =>
      // 413/415/403/非 JSON HTML 等均由 ApiError 携带后端中文 message。
      setError(err instanceof ApiError ? err.message : '上传失败，请重试'),
  });

  // 当前状态：优先展示本次上传结果，否则展示进入弹窗时的课程音频。
  const current = describeCourseAudio(uploadedAudio ?? course.audio);
  const disabled = mutation.isPending || !file;

  return (
    <Modal
      title={`导入课程音频 · ${course.name}`}
      description="上传单个 MP3，员工端学习中心可在线播放。仅接受 .mp3（audio/mpeg）。"
      onClose={onClose}
      footer={
        <>
          <button type="button" className={SECONDARY_BTN} onClick={onClose}>
            关闭
          </button>
          <button
            type="button"
            className={PRIMARY_BTN}
            disabled={disabled}
            onClick={() => {
              setError(null);
              mutation.mutate();
            }}
            data-testid="course-audio-submit"
          >
            {mutation.isPending ? '上传中…' : '上传音频'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        {/* 当前音频状态：未上传 / 已上传（文件名 / 大小 / 时长 / 更新时间）。 */}
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
          <div className="flex items-center gap-1.5 font-medium text-slate-700">
            <Music className="h-4 w-4 text-brand-600" />
            当前课程音频
          </div>
          {current.uploaded ? (
            <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1">
              <AudioMetaField label="文件名" value={current.fileName} />
              <AudioMetaField label="大小" value={current.size} />
              <AudioMetaField label="时长" value={current.duration} />
              <AudioMetaField label="更新时间" value={current.updatedAt} />
            </dl>
          ) : (
            <div className="mt-1 text-slate-400">尚未上传课程音频。</div>
          )}
        </div>

        {/* 选择文件 */}
        <label className={clsx(SECONDARY_BTN, 'cursor-pointer')}>
          <Music className="h-3.5 w-3.5" />
          选择 .mp3 文件
          <input
            type="file"
            accept=".mp3,audio/mpeg"
            className="hidden"
            data-testid="course-audio-input"
            onChange={(e) => {
              setError(null);
              setFile(e.target.files?.[0] ?? null);
            }}
          />
        </label>
        {file ? (
          <div className="text-xs text-slate-600">
            已选择：<span className="font-medium text-slate-800">{file.name}</span>（
            {formatFileSize(file.size)}）
          </div>
        ) : null}

        {error ? (
          <div className="rounded-md border border-rose-200 bg-rose-50 px-2.5 py-2 text-xs text-rose-700">
            {error}
          </div>
        ) : null}

        {uploadedAudio ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
            <div className="flex items-center gap-1.5 font-medium">
              <CheckCircle2 className="h-4 w-4" />
              上传完成，员工端学习中心已可播放。
            </div>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

function AudioMetaField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center gap-1">
      <dt className="text-slate-500">{label}</dt>
      <dd className="font-medium text-slate-800">{value}</dd>
    </div>
  );
}

// ─── 按课程导入一问一答 Markdown（预检 → 导入） ──────────────────────────
function ImportModal({
  course,
  onClose,
  onDone,
}: {
  course: CourseAdminView;
  onClose: () => void;
  onDone: () => Promise<unknown> | void;
}) {
  const [content, setContent] = useState('');
  const [strategy, setStrategy] = useState<CourseImportStrategy>('append');
  const [precheck, setPrecheck] = useState<CourseImportPrecheck | null>(null);
  const [result, setResult] = useState<CourseImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const precheckMutation = useMutation({
    mutationFn: () =>
      apiClient.post<CourseImportPrecheck>(
        `/admin/courses/${course.id}/import/precheck`,
        { content, strategy },
      ),
    onSuccess: (data) => {
      setError(null);
      setResult(null);
      setPrecheck(data);
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : '预检失败，请重试'),
  });

  const importMutation = useMutation({
    mutationFn: () =>
      apiClient.post<CourseImportResult>(`/admin/courses/${course.id}/import`, {
        content,
        strategy,
        dryRun: false,
      }),
    onSuccess: async (data) => {
      setError(null);
      setResult(data);
      await onDone();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : '导入失败，请重试'),
  });

  const onPickFile = (file: File | undefined) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setContent(typeof reader.result === 'string' ? reader.result : '');
      setPrecheck(null);
      setResult(null);
    };
    reader.readAsText(file);
  };

  const hasContent = content.trim().length > 0;
  // 导入前必须先预检（dryRun/precheck），且需解析到题目。
  const canImport =
    !importMutation.isPending && !!precheck && precheck.total > 0 && hasContent;

  return (
    <Modal
      wide
      title={`导入知识库 · ${course.name}`}
      description="一问一答 Markdown：先「预检」查看解析结果，再「确认导入」。"
      onClose={onClose}
      footer={
        <>
          <button type="button" className={SECONDARY_BTN} onClick={onClose}>
            关闭
          </button>
          <button
            type="button"
            className={SECONDARY_BTN}
            disabled={precheckMutation.isPending || !hasContent}
            onClick={() => {
              setError(null);
              precheckMutation.mutate();
            }}
          >
            {precheckMutation.isPending ? '预检中…' : '预检（不写库）'}
          </button>
          <button
            type="button"
            className={PRIMARY_BTN}
            disabled={!canImport}
            onClick={() => {
              setError(null);
              importMutation.mutate();
            }}
            title={
              !precheck
                ? '请先预检'
                : precheck.total === 0
                  ? '预检未识别到有效题目'
                  : undefined
            }
          >
            {importMutation.isPending
              ? '导入中…'
              : strategy === 'replace'
                ? '确认导入（替换本课程题库）'
                : '确认导入（追加）'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-3 text-xs text-slate-600">
            <span className="font-medium">导入策略</span>
            <label className="inline-flex items-center gap-1">
              <input
                type="radio"
                name="strategy"
                checked={strategy === 'append'}
                onChange={() => {
                  setStrategy('append');
                  setPrecheck(null);
                  setResult(null);
                }}
              />
              追加
            </label>
            <label className="inline-flex items-center gap-1">
              <input
                type="radio"
                name="strategy"
                checked={strategy === 'replace'}
                onChange={() => {
                  setStrategy('replace');
                  setPrecheck(null);
                  setResult(null);
                }}
              />
              替换（仅清空本课程现有题库）
            </label>
          </div>
          <label className={clsx(SECONDARY_BTN, 'cursor-pointer')}>
            <FileUp className="h-3.5 w-3.5" />
            选择 .md 文件
            <input
              type="file"
              accept=".md,.markdown,.txt,text/markdown,text/plain"
              className="hidden"
              onChange={(e) => onPickFile(e.target.files?.[0])}
            />
          </label>
        </div>

        <textarea
          className={clsx(INPUT, 'font-mono text-xs')}
          rows={8}
          placeholder={'### Q01：什么是细胞治疗？\n**答**：……\n\n---\n'}
          value={content}
          onChange={(e) => {
            setContent(e.target.value);
            // 内容变化后旧预检 / 结果失效，必须重新预检才能导入。
            setPrecheck(null);
            setResult(null);
          }}
        />

        {error ? (
          <div className="rounded-md border border-rose-200 bg-rose-50 px-2.5 py-2 text-xs text-rose-700">
            {error}
          </div>
        ) : null}

        {precheck ? <PrecheckPanel precheck={precheck} strategy={strategy} /> : null}

        {result ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
            <div className="flex items-center gap-1.5 font-medium">
              <CheckCircle2 className="h-4 w-4" />
              导入完成（{result.strategy === 'replace' ? '替换' : '追加'}）
            </div>
            <div className="mt-1 text-emerald-700">
              解析 {result.parsed} 题 · 新增 {result.created} 题
              {` · 跳过重复 ${result.skippedDuplicates} 题`}
              {result.removed > 0 ? ` · 替换移除 ${result.removed} 题` : ''} · 当前课程题量{' '}
              {result.questionCount}
            </div>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

function PrecheckPanel({
  precheck,
  strategy,
}: {
  precheck: CourseImportPrecheck;
  strategy: CourseImportStrategy;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        <Stat label="解析成功" value={precheck.total} tone="ok" />
        <Stat label="文件内重复标题" value={precheck.duplicateTitles.length} />
        <Stat label="文件内重复题干" value={precheck.duplicateQuestionBodies.length} />
        <Stat label="与现有题库重复" value={precheck.existingDuplicateBodies.length} />
        <Stat label="空答案" value={precheck.emptyAnswers.length} />
        <Stat label="解析失败" value={precheck.failures.length} tone="danger" />
      </div>

      {precheck.total === 0 ? (
        <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-amber-800">
          未识别到有效题目，请检查题目标题与答案格式。
        </div>
      ) : null}

      {precheck.duplicateTitles.length > 0 ? (
        <DetailLine label="重复标题" items={precheck.duplicateTitles} />
      ) : null}
      {precheck.existingDuplicateBodies.length > 0 ? (
        <DetailLine
          label={
            strategy === 'append'
              ? '与现有题库重复（追加导入会跳过）'
              : '与现有题库重复（替换导入会先软删除本课程旧题库）'
          }
          items={precheck.existingDuplicateBodies}
        />
      ) : null}
      {precheck.emptyAnswers.length > 0 ? (
        <DetailLine label="空答案题号" items={precheck.emptyAnswers} />
      ) : null}
      {precheck.failures.length > 0 ? (
        <div className="mt-2">
          <div className="font-medium text-rose-700">解析失败明细</div>
          <ul className="mt-1 space-y-0.5">
            {precheck.failures.map((f, idx) => (
              <li key={idx} className="text-rose-600">
                #{f.index}
                {f.questionCode ? ` (${f.questionCode})` : ''}：{f.reason}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {precheck.preview.length > 0 ? (
        <details className="mt-2">
          <summary className="cursor-pointer font-medium text-slate-700">
            预览前 {precheck.preview.length} 题
          </summary>
          <ul className="mt-1 space-y-1">
            {precheck.preview.map((p, idx) => (
              <li key={idx} className="rounded border border-slate-200 bg-white p-2">
                <div className="font-medium text-slate-800">
                  {p.questionCode}：{p.question}
                </div>
                <div className="mt-0.5 text-slate-500">答：{p.answer}</div>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'ok' | 'danger';
}) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="text-slate-500">{label}</span>
      <span
        className={clsx(
          'font-semibold',
          tone === 'ok' && 'text-emerald-700',
          tone === 'danger' && value > 0 && 'text-rose-700',
          (!tone || (tone === 'danger' && value === 0)) && 'text-slate-800',
        )}
      >
        {value}
      </span>
    </span>
  );
}

function DetailLine({ label, items }: { label: string; items: string[] }) {
  return (
    <div className="mt-2">
      <span className="font-medium text-slate-700">{label}：</span>
      <span className="text-slate-600">{items.join('、')}</span>
    </div>
  );
}
